import { Button, Chip, Input, Select, SelectItem, Spinner } from "@heroui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, ImageOff, LogOut, QrCode, TriangleAlert } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { thumbnailSrc } from "@/features/drawings/api";
import { listProcesses, type Process } from "@/features/processes/api";
import { errorMessage } from "@/lib/api";
import { logout } from "@/lib/auth";
import { useAuth } from "@/lib/auth-context";
import { canWrite, unauthorizedMessage } from "@/lib/authorization";
import { showError } from "@/lib/toast";

import { createWorkLog, listActiveMachines, resolveDrawingQr } from "./api";
import RecentWorkLogs from "./RecentWorkLogs";

/** よく入る作業時間。まず 1 タップで入り、違うときだけ数字を打つ。 */
const QUICK_HOURS = ["0.5", "1", "2", "4", "8"];

/** 前回選んだ工程・機械を覚えておく鍵（同じ人は同じものを選び続ける）。 */
const LAST_PROCESS_KEY = "work-log:last-process-id";
const LAST_MACHINE_KEY = "work-log:last-machine-id";

/** 端末の今日を YYYY-MM-DD で返す（作業日の初期値）。 */
function todayYmd(): string {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${m}-${d}`;
}

/**
 * 覚えた選択を読む。
 *
 * プライベートウィンドウや保存を止めた端末では読み書きが例外を投げるので、
 * 失敗しても画面は動くようにしておく（覚えていないだけで困らない）。
 */
function readRemembered(key: string): number | null {
  try {
    const raw = window.localStorage.getItem(key);
    const id = raw == null ? NaN : Number(raw);
    return Number.isInteger(id) ? id : null;
  } catch {
    return null;
  }
}

function remember(key: string, id: number | null): void {
  try {
    if (id == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, String(id));
  } catch {
    // 覚えられなくても次回に既定値が入らないだけ。
  }
}

/**
 * 現場での作業実績入力（スマホ）。図面に押した QR の着地点（`/q`）。
 *
 * 紙の QR はアプリの URL なので、**標準のカメラアプリで読むとこの画面が開く**
 * （[[work-log-mobile]]）。画面内にスキャナを持たないのは、カメラの取得
 * （`getUserMedia`）が https でしか動かず、LAN の IP で開く開発環境では確かめ
 * られないため。連続して読み取りたくなったら、そのときに足す。
 *
 * **何を作っているかは選ばせない。** QR が案件・明細・図面・版を指すので、ここで
 * 受け取るのは何をしたか（工程・機械・作業時間・作業日）だけ。
 *
 * `AppShell`（ヘッダ＋ドロワー）は被せない。横長のテーブル前提の枠を現場の
 * スマホに出す意味がないため、ログアウトだけを持つ細い帯を自前で置く。
 */
export default function WorkLogEntry() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, clear } = useAuth();
  const canRegister = canWrite(user, "workReport");

  // **QR の値は解釈せずそのまま API に渡す**（`?` の後ろをまるごと）。形式を
  // 知っているのは API 側だけにしておきたい。
  const code = location.search.replace(/^\?/, "");

  const [processId, setProcessId] = useState<number | null>(null);
  const [machineId, setMachineId] = useState<number | null>(null);
  const [duration, setDuration] = useState("");
  const [workDate, setWorkDate] = useState(todayYmd);
  const [saved, setSaved] = useState(false);
  // 登録のたびに増やして「最近の登録」を取り直す。
  const [reloadKey, setReloadKey] = useState(0);

  const resolved = useQuery({
    queryKey: ["drawing-qr", code],
    queryFn: () => resolveDrawingQr(code),
    enabled: code !== "",
    // 紙を読み直すまで結果は変わらない。
    staleTime: Infinity,
    retry: false,
  });

  const processes = useQuery({
    queryKey: ["processes", "active"],
    queryFn: () =>
      listProcesses({
        disabled: false,
        sortKey: "process_code",
        ascending: true,
        limit: 500,
        offset: 0,
      }).then((res) => res.items),
    staleTime: 5 * 60 * 1000,
  });

  const machines = useQuery({
    queryKey: ["machines", "active"],
    queryFn: listActiveMachines,
    staleTime: 5 * 60 * 1000,
  });

  // 前回の選択を初期値にする。候補が揃ってから当てるのは、今は無い（使用停止に
  // なった）工程・機械が選ばれたままになるのを防ぐため。
  useEffect(() => {
    if (processId != null || processes.data == null) return;
    const last = readRemembered(LAST_PROCESS_KEY);
    if (last != null && processes.data.some((p) => p.process_id === last)) {
      setProcessId(last);
    }
  }, [processes.data, processId]);

  useEffect(() => {
    if (machineId != null || machines.data == null) return;
    const last = readRemembered(LAST_MACHINE_KEY);
    if (last != null && machines.data.some((m) => m.machine_id === last)) {
      setMachineId(last);
    }
  }, [machines.data, machineId]);

  // 成功の合図は一定時間で消す（押して閉じる必要はない）。
  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(false), 1500);
    return () => clearTimeout(timer);
  }, [saved]);

  /**
   * 工程の並び。**見積で使った工程を先頭に寄せる**（内訳の行順）。
   *
   * その図面で見積った工程は実際にやる工程とほぼ一致するので、全工程から探させ
   * ない。見積が未紐付け（特急案件）なら、ただのコード順になる。
   */
  const sortedProcesses = useMemo<{ process: Process; quoted: boolean }[]>(() => {
    const all = processes.data ?? [];
    const quotedIds = resolved.data?.quotation_process_ids ?? [];
    const quoted = quotedIds
      .map((id) => all.find((p) => p.process_id === id))
      .filter((p): p is Process => p != null);
    const rest = all.filter((p) => !quotedIds.includes(p.process_id));
    return [
      ...quoted.map((process) => ({ process, quoted: true })),
      ...rest.map((process) => ({ process, quoted: false })),
    ];
  }, [processes.data, resolved.data]);

  const save = useMutation({
    mutationFn: () =>
      createWorkLog({
        project_item_id: resolved.data!.project_item_id,
        process_id: processId!,
        machine_id: machineId,
        duration,
        work_date: workDate,
        drawing_revision_id: resolved.data!.drawing_revision_id,
      }),
    onSuccess: () => {
      remember(LAST_PROCESS_KEY, processId);
      remember(LAST_MACHINE_KEY, machineId);
      // **工程と機械は残す**（続けて入れるときに選び直さなくて済む）。作業時間は
      // 毎回ちがうので消す。残したまま押されると、黙って嘘の実績が入る。
      setDuration("");
      setSaved(true);
      setReloadKey((k) => k + 1);
    },
    onError: (e) => showError(errorMessage(e, "実績の登録に失敗しました。")),
  });

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // サーバ側の失敗に関わらずローカルはログアウト扱いにする（AppHeader と同じ）。
    } finally {
      clear();
      navigate("/login", { replace: true });
    }
  };

  const hours = Number(duration);
  const durationValid = Number.isFinite(hours) && hours > 0 && hours <= 24;
  const ready = resolved.data != null && processId != null && durationValid;

  return (
    <div className="min-h-dvh bg-page">
      <header className="flex h-14 items-center gap-2 bg-brand-500 px-4 text-white">
        <QrCode className="size-6" />
        <span className="font-bold">作業実績の入力</span>
        <button
          type="button"
          aria-label="ログアウト"
          onClick={() => void handleLogout()}
          className="ml-auto rounded-md p-1.5 transition-colors hover:bg-white/20"
        >
          <LogOut className="size-5" />
        </button>
      </header>

      <main className="mx-auto flex max-w-[560px] flex-col gap-3 p-4">
        {/* (a) 読み取った対象。QR が無ければ読み方を出す。 */}
        {code === "" ? (
          <section className="flex flex-col items-center gap-3 rounded-lg border border-default-200 bg-white p-6 text-center">
            <QrCode className="size-10 text-default-400" />
            <p className="font-bold text-default-700">図面の QR を読み取ってください</p>
            <p className="text-sm text-default-500">
              スマートフォンのカメラで図面に押された QR を読み取ると、この画面が
              対象の図面付きで開きます。
            </p>
          </section>
        ) : resolved.isPending ? (
          <section className="flex items-center gap-2 rounded-lg border border-default-200 bg-white p-4 text-sm text-default-500">
            <Spinner size="sm" /> 図面を照合しています…
          </section>
        ) : resolved.error ? (
          <section className="flex flex-col gap-2 rounded-lg border border-danger-200 bg-danger-50 p-4">
            <p className="font-bold text-danger">図面を特定できませんでした</p>
            <p className="text-sm text-danger-600">
              {errorMessage(resolved.error, "QR を読み取れませんでした。")}
            </p>
          </section>
        ) : (
          <section className="flex flex-col gap-3 rounded-lg border border-default-200 bg-white p-4">
            <div className="flex items-baseline gap-2">
              <span className="text-xs text-default-500">工番</span>
              <span className="text-lg font-bold">{resolved.data.project_number}</span>
              {resolved.data.project_title && (
                <span className="truncate text-sm text-default-500">
                  {resolved.data.project_title}
                </span>
              )}
            </div>

            <div className="flex gap-3">
              {/* 図面は A3 横が多い。縦横比を固定して contain で収める
                  （cover にすると表題欄が切れる）。 */}
              <div className="flex aspect-4/3 w-32 shrink-0 items-center justify-center overflow-hidden rounded-md bg-default-50">
                {resolved.data.thumbnail_path ? (
                  <img
                    src={thumbnailSrc(resolved.data.thumbnail_path) ?? undefined}
                    alt={`${resolved.data.drawing_number} の図面`}
                    className="size-full object-contain"
                  />
                ) : (
                  <ImageOff className="size-6 text-default-300" />
                )}
              </div>

              <dl className="min-w-0 flex-1 text-sm">
                <dt className="text-xs text-default-500">図番</dt>
                <dd className="font-bold">{resolved.data.drawing_number}</dd>
                <dt className="mt-1 text-xs text-default-500">品名</dt>
                <dd className="truncate">{resolved.data.item_name}</dd>
                <dt className="mt-1 text-xs text-default-500">数量</dt>
                <dd>
                  {resolved.data.quantity}
                  <span className="ml-2 text-xs text-default-500">
                    版 {resolved.data.revision}
                    {resolved.data.page > 1 && ` / ${resolved.data.page}ページ目`}
                  </span>
                </dd>
              </dl>
            </div>

            {!resolved.data.is_latest_revision && (
              // **止めない。** 古い紙で作ってしまった作業も、作業としては起きて
              // いる。気づけるように出すだけ。
              <div className="flex items-start gap-2 rounded-md bg-warning-50 p-2 text-sm text-warning-700">
                <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                <span>
                  この紙は最新の版ではありません。登録はできますが、図面を確認して
                  ください。
                </span>
              </div>
            )}
          </section>
        )}

        {/* (b) 何をしたかの入力 */}
        <section className="flex flex-col gap-4 rounded-lg border border-default-200 bg-white p-4">
          <p className="text-sm font-bold text-default-700">作業の内容</p>

          <Select
            label="工程"
            labelPlacement="outside"
            placeholder={processes.isPending ? "読み込み中…" : "工程を選択"}
            isLoading={processes.isPending}
            isDisabled={processes.data == null}
            selectedKeys={processId != null ? [String(processId)] : []}
            onSelectionChange={(keys) => {
              const key = Array.from(keys)[0] as string | undefined;
              setProcessId(key == null ? null : Number(key));
            }}
          >
            {sortedProcesses.map(({ process, quoted }) => (
              <SelectItem key={process.process_id} textValue={process.process_name}>
                <span className="flex items-center gap-2">
                  <span>{process.process_name}</span>
                  {quoted && (
                    <Chip size="sm" variant="flat" color="primary">
                      見積
                    </Chip>
                  )}
                </span>
              </SelectItem>
            ))}
          </Select>

          <Select
            label="工作機械（使った場合）"
            labelPlacement="outside"
            placeholder={machines.isPending ? "読み込み中…" : "機械を選択"}
            isLoading={machines.isPending}
            isDisabled={machines.data == null || machines.data.length === 0}
            selectedKeys={machineId != null ? [String(machineId)] : []}
            onSelectionChange={(keys) => {
              const key = Array.from(keys)[0] as string | undefined;
              setMachineId(key == null ? null : Number(key));
            }}
          >
            {(machines.data ?? []).map((machine) => (
              <SelectItem key={machine.machine_id} textValue={machine.machine_name}>
                {machine.machine_name}
              </SelectItem>
            ))}
          </Select>

          <div className="flex flex-col gap-2">
            <span className="text-sm">作業時間（時間）</span>
            {/* よく使う値は 1 タップ。手袋でも押せるよう高さを詰めない。 */}
            <div className="flex flex-wrap gap-2">
              {QUICK_HOURS.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setDuration(value)}
                  className={`h-11 min-w-16 rounded-md border px-3 font-bold transition-colors ${
                    duration === value
                      ? "border-brand-500 bg-brand-500 text-white"
                      : "border-default-300 bg-white text-default-700"
                  }`}
                >
                  {value}
                </button>
              ))}
            </div>
            <Input
              type="number"
              aria-label="作業時間（時間）"
              placeholder="直接入力（0.5 刻み）"
              step="0.5"
              min="0.5"
              max="24"
              inputMode="decimal"
              value={duration}
              onValueChange={setDuration}
              endContent={<span className="text-sm text-default-500">時間</span>}
              isInvalid={duration !== "" && !durationValid}
              errorMessage="0 より大きく 24 以下の時間を入れてください。"
            />
          </div>

          <Input
            type="date"
            label="作業日"
            labelPlacement="outside"
            value={workDate}
            onValueChange={setWorkDate}
            max={todayYmd()}
          />

          {!canRegister && (
            <p className="text-sm text-danger">{unauthorizedMessage("workReport")}</p>
          )}

          <Button
            color="primary"
            size="lg"
            className="h-14 text-base font-bold"
            isDisabled={!ready || !canRegister}
            isLoading={save.isPending}
            onPress={() => save.mutate()}
          >
            この作業を登録する
          </Button>
        </section>

        {/* (c) 二重登録に気づくための一覧 */}
        <RecentWorkLogs reloadKey={reloadKey} />
      </main>

      {saved && (
        // 登録できたことを画面いっぱいで示す（作業の手を止めずに確認できる）。
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-black/60 text-white"
          onClick={() => setSaved(false)}
        >
          <CheckCircle2 className="size-16" />
          <p className="text-lg font-bold">登録しました</p>
        </div>
      )}
    </div>
  );
}
